import { decode, encode } from '@msgpack/msgpack'
import { isNil, omitBy, pick } from 'lodash-es'
import type { ValueOf } from 'type-fest'
import type {
  ArtworkDetailFragment,
  DetailedTaskParameters,
  Task,
} from 'src/services/apollo/generated/graphql'
import type { taskParameterFieldsCollection } from 'src/services/helpers/taskParams'
import { taskParameterFieldsForTaskList } from 'src/services/helpers/taskParams'
import type { TaskSubmitFormValues } from '../BaseTaskSubmitForm'

/** Base64 "URL safe" variant */
function urlSafeEncode(str: string) {
  return str.replaceAll('/', '_').replaceAll('+', '-')
}
function urlSafeDecode(str: string) {
  return str.replaceAll('_', '/').replaceAll('-', '+')
}

function base64ToBytes(base64: string): Uint8Array {
  const decoded = urlSafeDecode(base64)
  if (typeof window !== 'undefined') {
    const b64String = window.atob(decoded)
    return Uint8Array.from(b64String, m => m.codePointAt(0)!)
  }
  return new Uint8Array(Buffer.from(decoded, 'base64'))
}

function bytesToBase64(bytes: Uint8Array): string {
  if (typeof window !== 'undefined') {
    const binString = String.fromCodePoint(...bytes)
    return urlSafeEncode(window.btoa(binString))
  }
  return urlSafeEncode(Buffer.from(bytes).toString('base64'))
}

const msgpack = {
  encode: (obj: unknown) => encode(obj),
  decode: (buf: Uint8Array) => decode(buf),
  encodeBase64: (obj: unknown) => bytesToBase64(encode(obj)),
  decodeBase64: (buf: string) => decode(base64ToBytes(buf)),
}

function encodeImportAsParams(obj: any) {
  if (!obj) return ''
  return msgpack.encodeBase64(obj)
}

function decodeImportAsParams(str: string) {
  if (!str) return undefined
  try {
    const decoded = msgpack.decodeBase64(str)
    return decoded
  } catch (err) {
    // Decode failure, return undefined
    // TODO: Consider logging it to external services
  }
}

export function encodeArtworkForImport(
  artwork?: Pick<ArtworkDetailFragment, 'id' | 'prompts' | 'extra' | 'media'>,
) {
  // Remove unnecessary fields to shorten query string's length
  const strippedMedia = omitBy(artwork?.media, isNil)
  const stripped = {
    ...pick(artwork, ['id', 'prompts', 'hidePrompts']),
    media: strippedMedia,
    extra: pick(artwork?.extra, ['naturalPrompts']),
  }
  return encodeImportAsParams(stripped)
}

export function encodeTaskForImport(task?: Task | DetailedTaskParameters) {
  if (!task) return null

  // Remove unnecessary fields to shorten query string's length
  const strippedMedia = 'media' in task ? omitBy(task.media, isNil) : undefined
  const stripped = {
    ...omitBy(
      pick(task, ['id', 'outputs', 'parameters', 'favoritedAt']),
      isNil,
    ),
    media: strippedMedia,
  }
  return encodeImportAsParams(stripped)
}

export function encodeInitialValuesForImport(
  initialValues: TaskSubmitFormValues,
) {
  return encodeImportAsParams(parseInitialValuesForImport(initialValues))
}

export function encodeCharacterForImport({
  characterId,
}: {
  characterId: string
}) {
  return encodeImportAsParams({ characterId })
}

export function decodeTaskForImport(str: string) {
  return decodeImportAsParams(str) as
    | (Partial<Task> & { id: string })
    | undefined
}

export function decodeArtworkForImport(str: string) {
  return decodeImportAsParams(str) as Partial<ArtworkDetailFragment> | undefined
}

export function decodeInitialValuesForImport(str: string) {
  return decodeImportAsParams(str) as Partial<TaskSubmitFormValues> | undefined
}

export function decodeCharacterForImport(str: string) {
  return decodeImportAsParams(str) as { characterId: string } | undefined
}

/**
 * Remove non-primitive values that can't be reliably encoded
 * @param param0
 */
function parseInitialValuesForImport(initialValues: TaskSubmitFormValues) {
  // Reusing parameters fields for task list
  const allowedFields = taskParameterFieldsForTaskList satisfies ValueOf<
    typeof taskParameterFieldsCollection
  >

  /**
   * Removing undefined values to avoid encoding them to null,
   * per msgpack's spec
   *
   * @see https://github.com/msgpack/msgpack-javascript/blob/main/README.md#messagepack-mapping-table
   */
  return omitBy(pick(initialValues, allowedFields), v => v === undefined)
}
